"use client";

import { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PostImageGridProps {
  urls: string[];
  className?: string;
}

export default function PostImageGrid({ urls, className }: PostImageGridProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const items = urls.filter(Boolean).slice(0, 4);
  if (items.length === 0) return null;

  const gridClass =
    items.length === 1
      ? 'grid-cols-1'
      : items.length === 2
      ? 'grid-cols-2'
      : 'grid-cols-2';

  const aspect = items.length === 1 ? 'aspect-[16/10]' : 'aspect-square';

  return (
    <>
      <div
        className={cn(
          'grid gap-1 rounded-sm overflow-hidden border border-border-subtle',
          gridClass,
          className
        )}
      >
        {items.map((url, i) => {
          const spanThird = items.length === 3 && i === 0;
          return (
            <button
              key={url + i}
              type="button"
              onClick={() => setLightboxIndex(i)}
              className={cn(
                'relative bg-surface-3 overflow-hidden focus:outline-none focus:ring-2 focus:ring-accent',
                aspect,
                spanThird && 'row-span-2 aspect-auto'
              )}
              aria-label={`View image ${i + 1} of ${items.length}`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={url}
                alt=""
                className="w-full h-full object-cover hover:opacity-95 transition-opacity"
                loading="lazy"
              />
            </button>
          );
        })}
      </div>

      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(null);
            }}
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={items[lightboxIndex]}
            alt=""
            className="max-w-full max-h-full object-contain rounded-sm"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
